import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import Cookies from "js-cookie";

const AddMemberModal = ({ isOpen, onClose, onAddMember }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Researcher");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [orgDetails, setOrgDetails] = useState(null);

  const validateEmail = (email) => /^\S+@\S+\.\S+$/.test(email);

  useEffect(() => {
    const fetchOrgDetails = async () => {
      const token = Cookies.get("token");
      if (!token) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/project/getuserPlanAndProject`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              token: token,
            },
          }
        );

        const data = await response.json();
        if (data.status === "success") {
          setOrgDetails({
            org_name: data.org_name,
            userName: data.username,
          });
        }
      } catch (err) {
        console.error("Error fetching org details:", err);
      }
    };

    if (isOpen) {
      fetchOrgDetails();
    }
  }, [isOpen]);

  const handleAddMember = async () => {
    if (!email.trim()) {
      setError("Please enter an email address");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (!orgDetails?.org_name) {
      setError("Organization details not loaded. Please try again.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = Cookies.get("token");

      const payload = {
        email: email.toLowerCase(),
        role,
        org_name: orgDetails.org_name,
        userName: orgDetails.userName,
      };

      const inviteResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/sendInvite`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );
      const inviteData = await inviteResponse.json();
      if (inviteData.status === "success") {
        const newMember = {
          email: email.toLowerCase(),
          role: role,
          id: Date.now(),
          name: email.split("@")[0],
          initials: email.substring(0, 2).toUpperCase(),
          projects: 0,
          status: "pending",
        };

        onAddMember?.(newMember);

        setEmail("");
        setRole("Researcher");
        onClose();
      } else {
        setError(inviteData.message || "Failed to send invitation");
      }
    } catch (err) {
      console.error("Error adding member:", err);
      setError("Failed to add member. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setError("");
    setIsEmailValid(validateEmail(value));
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
      }}
    >
      <div
        style={{
          backgroundColor: "#1f2937",
          borderRadius: "12px",
          width: "90%",
          maxWidth: "500px",
          color: "white",
          boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "1.2rem 1.5rem",
            borderBottom: "1px solid #374151",
          }}
        >
          <div>
            <h2 style={{ fontSize: "1.25rem", marginBottom: "0.3rem" }}>
              Add Member
            </h2>
            <p style={{ color: "#9ca3af", fontSize: "0.875rem" }}>
              Add a new member to your organization
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#9ca3af",
              cursor: "pointer",
            }}
          >
            <X size={22} />
          </button>
        </div>
        <div style={{ padding: "1.5rem" }}>
          <label style={{ display: "block", marginBottom: "0.5rem" }}>
            Email Address *
          </label>

          <input
            type="email"
            value={email}
            onChange={handleChange}
            placeholder="member@example.com"
            style={{
              width: "100%",
              padding: "0.75rem 1rem",
              borderRadius: "8px",
              border: `1px solid ${error ? "#ef4444" : "#4b5563"}`,
              backgroundColor: "#111827",
              color: "white",
              marginBottom: "1rem",
            }}
          />

          <label style={{ display: "block", marginBottom: "0.5rem" }}>
            Role *
          </label>

          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            style={{
              width: "100%",
              padding: "0.75rem 1rem",
              borderRadius: "8px",
              border: "1px solid #4b5563",
              backgroundColor: "#111827",
              color: "white",
              marginBottom: "0.75rem",
              cursor: "pointer",
            }}
          >
            <option value="Researcher">Researcher</option>
            <option value="Admin">Admin</option>
          </select>

          {error && (
            <p style={{ color: "#ef4444", marginBottom: "0.75rem" }}>{error}</p>
          )}

          <div
            style={{
              marginTop: "1rem",
              padding: "1rem",
              backgroundColor: "rgba(59,130,246,0.1)",
              border: "1px solid rgba(59,130,246,0.2)",
              borderRadius: "8px",
            }}
          >
            <p style={{ margin: 0, fontSize: "0.875rem", color: "#94a3b8" }}>
              An invitation email will be sent to the member. They will join
              as a <strong>{role}</strong>.
            </p>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "1rem 1.5rem",
            borderTop: "1px solid #374151",
            gap: "0.75rem",
          }}
        >
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "6px",
              backgroundColor: "#374151",
              color: "#e5e7eb",
              border: "none",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleAddMember}
            disabled={!isEmailValid || loading}
            style={{
              padding: "0.6rem 1.2rem",
              borderRadius: "6px",
              border: "none",
              cursor: isEmailValid && !loading ? "pointer" : "not-allowed",
              backgroundColor: isEmailValid
                ? loading
                  ? "#2563ebaa"
                  : "#2563eb"
                : "#1e293b",
              color: "white",
            }}
          >
            {loading ? "Sending Invite..." : "Send Invitation"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMemberModal;
